import { Text } from "@shopify/polaris";
import { ISODateString } from "@smartrr/shared/entities/ISODateString";
import { IOrganization } from "@smartrr/shared/entities/Organization";
import { IPurchaseStateWithCustomerRelationship } from "@smartrr/shared/entities/PurchaseState";
import { ISmartrrSellingPlanGroup } from "@smartrr/shared/entities/SellingPlanGroup";
import { viewShopifyId } from "@smartrr/shared/utils/ensureShopifyGid";
import { IdContainer } from "@vendor-app/app/_sharedComponents/TypedTable/usePolarisTypedTable";
import { pluralize } from "@smartrr/shared/utils/pluralize";

import {
  getCPSStatusBadge,
  getNextDelivery,
  openSubscriptionDetails,
  getFormattedUpcomingDate,
  ItemsPopover,
} from "@vendor-app/app/AdminRoute/AdminSubscriptionDetailsRoute/libs";
import { getTotalDisplay } from "@smartrr/shared/utils/displayUtils";

import { flatten, isInteger } from "lodash";
import { HomePageTableTab } from "@vendor-app/app/AdminRoute/AdminHomeRoute/utils";

export const parseHomeTableData = (
  purchaseStates: (IPurchaseStateWithCustomerRelationship & { cancellationReason?: string | undefined })[],
  sellingPlanGroups: ISmartrrSellingPlanGroup[],
  activeOrg: IOrganization | null,
  selectedTab: HomePageTableTab
) => {
  return purchaseStates.map((purchaseState, index) => {
    const { initialSubmissionDate, custRel, purchaseStateStatus, shopifyId, stLineItems, sellingPlanId } =
      purchaseState;
    const createdDateWithZone = ISODateString.fromString(initialSubmissionDate).setZone(
      activeOrg?.billingTimezone
    );

    const nextDelivery = getNextDelivery(purchaseState, activeOrg);
    const nextOrderHasAddons =
      nextDelivery &&
      purchaseState.stLineItems.some(
        line => isInteger(line.skdIdx) && line.skdIdx === nextDelivery.indexFromScheduleStart
      );

    if (selectedTab === HomePageTableTab.CANCELLED) {
      return {
        id: String(purchaseState.id),
        shopifyId: (
          <IdContainer onClick={() => openSubscriptionDetails(purchaseState)}>
            <Text variant="bodyMd" as="span" fontWeight="semibold">
              #{viewShopifyId(shopifyId)}
            </Text>
          </IdContainer>
        ),
        emailOrName: `${custRel?.firstName} ${custRel?.lastName}`,
        email: custRel?.email,
        estimatedTotal:
          nextDelivery?.paymentMultipleDueOnDate === 0 && !nextOrderHasAddons
            ? "Prepaid"
            : getTotalDisplay(
                nextDelivery?.paymentMultipleDueOnDate ?? 1,
                purchaseState,
                nextDelivery?.indexFromScheduleStart ?? 0,
                true,
                nextDelivery?.indexFromNext
              ),
        items: (
          <ItemsPopover
            stLineItems={purchaseState.stLineItems.filter(
              item =>
                (item.isAddOn || item.isRedeemed ? item.skdIdx === nextDelivery?.indexFromScheduleStart : true) &&
                !item.isRedeemed
            )}
            activatorTitle={pluralize(
              stLineItems
                .filter(
                  item =>
                    (item.isAddOn || item.isRedeemed
                      ? item.skdIdx === nextDelivery?.indexFromScheduleStart
                      : true) && !item.isRedeemed
                )
                .reduce((acc, prev) => (acc += prev.quantity), 0),
              "item"
            )}
            itemIndex={index}
          />
        ),
        cancellationReason: purchaseState.cancellationReason ?? "No reason selected",
        action: getCPSStatusBadge(purchaseStateStatus),
        createdDate: createdDateWithZone
          ? createdDateWithZone.toLocaleString({
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : "---",
      };
    }

    return {
      id: String(purchaseState.id),
      shopifyId: (
        <IdContainer onClick={() => openSubscriptionDetails(purchaseState)}>
          <Text variant="bodyMd" as="span" fontWeight="semibold">
            #{viewShopifyId(shopifyId)}
          </Text>
        </IdContainer>
      ),
      upcomingOrderDate: getFormattedUpcomingDate(purchaseState, activeOrg),
      emailOrName: `${custRel?.firstName} ${custRel?.lastName}`,
      email: custRel?.email,
      estimatedTotal:
        nextDelivery?.paymentMultipleDueOnDate === 0 && !nextOrderHasAddons
          ? "Prepaid"
          : getTotalDisplay(
              nextDelivery?.paymentMultipleDueOnDate ?? 1,
              purchaseState,
              nextDelivery?.indexFromScheduleStart ?? 0,
              true,
              nextDelivery?.indexFromNext
            ),
      items: (
        <ItemsPopover
          stLineItems={purchaseState.stLineItems.filter(
            item =>
              (item.isAddOn || item.isRedeemed ? item.skdIdx === nextDelivery?.indexFromScheduleStart : true) &&
              !item.isRedeemed
          )}
          activatorTitle={pluralize(
            stLineItems
              .filter(
                item =>
                  (item.isAddOn || item.isRedeemed
                    ? item.skdIdx === nextDelivery?.indexFromScheduleStart
                    : true) && !item.isRedeemed
              )
              .reduce((acc, prev) => (acc += prev.quantity), 0),
            "item"
          )}
          itemIndex={index}
        />
      ),
      status: getCPSStatusBadge(
        purchaseState.subProperties?.billingFailedMessage && purchaseStateStatus === "PAUSED"
          ? "FAILED"
          : purchaseStateStatus
      ),
      createdDate: createdDateWithZone
        ? createdDateWithZone.toLocaleString({
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        : "---",
      sellingPlanId:
        flatten(sellingPlanGroups.map(spg => spg.sellingPlans)).find(sp => sp.shopifyId === sellingPlanId)
          ?.name ?? "---",
    };
  });
};
